import Reveal from "@/components/Reveal";
import Tricolor from "@/components/Tricolor";
import { MapPin, Baby, UtensilsCrossed, Phone, Navigation, Waypoints, CircleParking } from "lucide-react";

const MAP_EMBED =
  "https://maps.google.com/maps?q=Via%20Appennini%2044%2FC%2C%2021034%20Cocquio-Trevisago%20VA&t=&z=16&ie=UTF8&iwloc=&output=embed";
const DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Via+Appennini+44%2FC%2C+21034+Cocquio-Trevisago+VA";

const cardCls = "rounded-2xl border border-[#ECECEA] bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#E30613]/40 hover:shadow-lg";

const Venue = () => (
  <section id="location" className="mx-auto max-w-7xl px-6 py-28 sm:py-36" data-testid="venue-section">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E30613]">Location & Info</p>
      <Tricolor className="mt-4" />
      <h2 className="mt-6 font-display text-2xl font-semibold uppercase tracking-[0.06em] text-[#232525] sm:text-3xl lg:text-4xl">
        Vi aspettiamo <span className="text-[#E30613]">in sede</span>
      </h2>
    </Reveal>

    <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
      <Reveal>
        <div className="group relative h-full min-h-[440px] overflow-hidden rounded-2xl border border-[#ECECEA] shadow-sm" data-testid="venue-image-card">
          <img src="/sede-tecnosystem.webp" alt="La sede Tecnosystem di Cocquio-Trevisago" className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/80">La Sede</p>
            <h3 className="mt-2 font-display text-2xl font-semibold uppercase tracking-[0.05em] text-white">Tecnosystem SRL</h3>
            <p className="mt-2 flex items-center gap-2 text-sm font-light text-white/85">
              <MapPin size={14} className="text-[#FF5A64]" /> Via Appennini, 44/C · 21034 Cocquio-Trevisago (VA)
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="relative h-full min-h-[440px] overflow-hidden rounded-2xl border border-[#ECECEA] shadow-sm" data-testid="venue-map-card">
          <iframe
            title="Mappa — Tecnosystem SRL, Via Appennini 44/C, Cocquio-Trevisago (VA)"
            src={MAP_EMBED}
            className="absolute inset-0 h-full w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="venue-map-iframe"
          />
          <a
            href={DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-directions-button"
            className="absolute bottom-5 left-5 flex items-center gap-2 rounded-full bg-[#E30613] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white shadow-lg transition-all duration-300 hover:bg-[#B00410] hover:shadow-[0_10px_28px_rgba(227,6,19,0.4)]"
          >
            <Navigation size={14} /> Indicazioni stradali
          </a>
        </div>
      </Reveal>
    </div>

    <Reveal delay={0.1}>
      <div className="mt-5 grid overflow-hidden rounded-2xl border border-[#ECECEA] bg-white shadow-sm md:grid-cols-2" data-testid="parking-card">
        <div className="relative min-h-[340px]">
          <img
            src="/parcheggio-tecnosystem.jpg"
            alt="Vista satellitare: percorso da Via Appennini al parcheggio dedicato e alla sede Tecnosystem"
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="parking-image"
          />
        </div>
        <div className="p-8 sm:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E30613]">Come raggiungerci</p>
          <h3 className="mt-3 font-display text-xl font-semibold uppercase tracking-[0.05em] text-[#232525]">Parcheggio dedicato agli ospiti</h3>
          <div className="mt-8 space-y-7">
            <div className="flex gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#EAB308]/60 bg-[#FDE68A]/40">
                <Waypoints size={17} className="text-[#A16207]" />
              </span>
              <div>
                <p className="text-sm font-bold text-[#232525]">Il percorso giallo · Via Appennini</p>
                <p className="mt-1 text-sm font-light leading-relaxed text-[#5B5D5D]">Entra da Via Appennini e prosegui seguendo il tratto evidenziato in giallo nella foto.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#EAB308]/60 bg-[#FDE68A]/40">
                <CircleParking size={17} className="text-[#A16207]" />
              </span>
              <div>
                <p className="text-sm font-bold text-[#232525]">Il quadrato giallo · Parcheggio</p>
                <p className="mt-1 text-sm font-light leading-relaxed text-[#5B5D5D]">È il parcheggio dedicato, riservato a tutti gli ospiti dell'Open Day.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E30613]/50 bg-[#FDECEA]">
                <MapPin size={17} className="text-[#E30613]" />
              </span>
              <div>
                <p className="text-sm font-bold text-[#232525]">Il cerchio rosso · La sede Tecnosystem</p>
                <p className="mt-1 text-sm font-light leading-relaxed text-[#5B5D5D]">Due passi a piedi dal parcheggio e sei arrivato da noi.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>

    <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
      <Reveal delay={0.1}>
        <div className={cardCls} data-testid="families-card">
          <Baby size={20} className="text-[#E30613]" />
          <h3 className="mt-4 font-display text-lg font-semibold uppercase tracking-[0.05em] text-[#232525]">Famiglie & Bambini</h3>
          <p className="mt-2 text-sm font-bold text-[#E30613]">Animazione dedicata</p>
          <p className="mt-2 text-sm font-light text-[#5B5D5D]">Portate tutta la famiglia: i più piccoli avranno un intrattenimento dedicato.</p>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className={cardCls} data-testid="buffet-card">
          <UtensilsCrossed size={20} className="text-[#E30613]" />
          <h3 className="mt-4 font-display text-lg font-semibold uppercase tracking-[0.05em] text-[#232525]">Buffet & Ospitalità</h3>
          <p className="mt-2 text-sm font-bold text-[#E30613]">Buffet durante tutta la giornata</p>
          <p className="mt-2 text-sm font-light text-[#5B5D5D]">E alle 16:00 il taglio della torta celebrativa con brindisi finale.</p>
        </div>
      </Reveal>

      <Reveal delay={0.3}>
        <div className={cardCls} data-testid="contact-card">
          <Phone size={20} className="text-[#E30613]" />
          <h3 className="mt-4 font-display text-lg font-semibold uppercase tracking-[0.05em] text-[#232525]">Contatti</h3>
          <p className="mt-2 text-sm font-light text-[#3A3C3C]">marketing@tecnosystem1981.com</p>
          <p className="mt-1 text-sm font-light text-[#5B5D5D]">0332 771488</p>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Venue;
